// Mock/static content for Elevate Medical Clinics
export const brand = {
  name: 'Elevate Medical Clinics',
  short: 'Elevate',
  formerly: 'Previously Rensburg Medical Clinic',
  tagline: 'Root Cause. Real Solutions. Lasting Health.',
  phone: '+61 432 809 498',
  phoneShort: '0432 809 498',
  email: 'reception@elevatemedicalclinics.com.au',
  address: 'Unit 6, 689 Beaufort Street, Mount Lawley, WA 6050',
  hours: [
    ['Monday', '9:00am – 5:00pm'],
    ['Tuesday', '9:00am – 5:00pm'],
    ['Wednesday', '9:00am – 5:00pm'],
    ['Thursday', '9:00am – 5:00pm'],
    ['Friday', '9:00am – 5:00pm'],
    ['Saturday', 'Closed'],
    ['Sunday', 'Closed'],
  ],
  mapEmbed:
    'https://www.google.com/maps?q=Unit+6+689+Beaufort+Street+Mount+Lawley+WA+6050&output=embed',
  bookingUrl:
    'https://www.hotdoc.com.au/medical-centres/mount-lawley-WA-6050/rensburg-medical-clinic/doctors',
};

export const heroStats = [
  { value: '30+', label: 'Years elevating health' },
  { value: '1,000+', label: 'Patients transformed' },
  { value: '6', label: 'Clinical programs' },
  { value: '30+', label: 'Data points per assessment' },
];

export const pathway = {
  id: 'pw1',
  eyebrow: 'Our Care Model',
  title: 'Comprehensive Lifestyle & Medical Assessment',
  lead: 'Dr Anthony Chaffee',
  description:
    'A single integrated pathway that combines an in-depth medical assessment, lifestyle & nutrition review, extensive investigations and a personalised, root-cause treatment plan — delivered by a multidisciplinary team.',
  bullets: [
    'In-depth medical assessment & history',
    'Lifestyle, nutrition & performance review',
    'Extensive investigations & blood work',
    'Root-cause analysis with functional lens',
    'Personalised, written treatment plan',
    'Referral to the right specialist program',
  ],
  perfectFor: [
    'Complex or unresolved symptoms',
    'Anyone wanting a comprehensive, root-cause approach',
    'Preventative & longevity-focused care',
    'Ongoing GP care with a lifestyle-medicine lens',
    'Chronic disease management',
    'People seeking a second opinion',
  ],
};

export const programs = [
  { id: 'p1', n: '01', title: 'Metabolic Health Program', tag: 'Common conditions',
    conditions: ['Obesity', 'Type 2 diabetes', 'Prediabetes', 'Insulin resistance', 'Metabolic syndrome', 'Fatty liver (NAFLD)', 'Dyslipidemia', 'Hypertension', 'Cardiovascular risk'],
    focus: 'Improve metabolic health, regulate blood sugar, reduce inflammation & lower chronic disease risk.',
    image: 'https://images.unsplash.com/photo-1606206591513-adbfbdd7a177' },
  { id: 'p2', n: '02', title: 'Hormone & Endocrine Health', tag: 'Common conditions',
    conditions: ["Women's Health: PCOS, Menopause, Perimenopause, Low testosterone, Andropause", 'Endocrine: Hashimoto’s thyroiditis, Hypothyroidism, Graves’ disease, Adrenal dysfunction, Hormonal weight gain'],
    focus: 'Balance hormones, support thyroid & adrenal health and improve energy, mood, body composition & wellbeing.',
    image: 'https://images.unsplash.com/photo-1572884267966-02340ebc90ac' },
  { id: 'p3', n: '03', title: 'Gut, Immune & Inflammatory Health', tag: 'Common conditions',
    conditions: ['Digestive: IBS, Reflux / GORD, Constipation, Diarrhoea, H. pylori, SIBO', 'Autoimmune & Inflammatory: Hashimoto’s thyroiditis, Rheumatoid arthritis, Psoriasis, Lupus, Ankylosing spondylitis, Multiple sclerosis, Inflammatory bowel disease, Chronic inflammation'],
    focus: 'Heal the gut, calm inflammation, support immune balance and address the root causes of digestive & autoimmune issues.',
    image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528' },
  { id: 'p4', n: '04', title: 'Preventative Health & Longevity', tag: 'Common goals',
    conditions: ['Healthy aging', 'Disease prevention', 'Optimising blood markers', 'Heart health', 'Brain health', 'Bone health', 'Energy & vitality', 'Performance optimisation', 'Family history risk management'],
    focus: 'Optimise health, prevent disease and support long-term vitality, resilience & performance.',
    image: 'https://images.unsplash.com/photo-1633167606207-d840b5070fc2' },
  { id: 'p5', n: '05', title: 'Precision Clinical Nutrition', tag: 'Nutrition support for',
    conditions: ['Weight management', 'Therapeutic ketogenic nutrition', 'Carnivore nutrition', 'Type 2 diabetes', 'Heart health', 'Gut health', "Women's health", 'Sports & performance', 'Behaviour change', 'Meal planning', 'Supplement implementation', 'Nutrient deficiencies'],
    focus: 'Personalised nutrition to support your health goals using evidence-based, low-carbohydrate strategies.',
    image: 'https://images.unsplash.com/photo-1585358682246-23acb1561f6b' },
  { id: 'p6', n: '06', title: 'Complex & Chronic Conditions', tag: 'Common conditions',
    conditions: ['Chronic fatigue', 'Long COVID (if applicable)', 'Fibromyalgia', 'Persistent unexplained symptoms', 'Multiple chronic conditions', 'Autoimmune disease', 'When you’ve “tried everything”'],
    focus: 'Comprehensive, individualised care for complex cases where standard approaches have not delivered results.',
    image: 'https://images.unsplash.com/photo-1678940805259-e2be79fa33e4' },
];

export const included = [
  { title: 'Medical Assessment', desc: 'Detailed consultation with our lead physician, Dr Anthony Chaffee.' },
  { title: 'Pathology & Investigations', desc: 'In-depth bloods and functional testing where clinically appropriate.' },
  { title: 'Personalised Treatment Plan', desc: 'A written, tailored plan built around your unique data and goals.' },
  { title: 'Dietitian Coordination', desc: 'Coordinated referral to Sanjana Kaur, our Accredited Practising Dietitian.' },
  { title: 'Ongoing Reviews & Monitoring', desc: 'Regular check-ins to track progress and adjust care.' },
  { title: 'Adjustments to Your Program', desc: 'Adaptive care based on your response, life and evolving goals.' },
];

export const team = [
  {
    id: 't1',
    name: 'Dr Zaheer Mohamed',
    role: 'DBA · Founder & CEO',
    creds: 'DBA · Multinational private equity',
    languages: 'English',
    focus: ['Healthcare leadership', 'Governance', 'Premium care model', 'Strategy'],
    bio:
      'A nationally respected health executive, Zaheer brings a unique blend of leadership and healthcare insight to Elevate. With past executive roles across the public and private sector, his vision and governance continue to shape Elevate\u2019s premium model of care.',
    quote: 'Healthcare must evolve. We\u2019re building a system around people, not problems.',
    image: 'https://customer-assets.emergentagent.com/job_elevate-refresh-1/artifacts/yayerfjm_Adobe%20Express%202026-07-10%2012.03.07.png',
    hasBackground: false,
    photoScale: 1.4,
    photoOrigin: 'center 38%',
  },
  {
    id: 't2',
    name: 'Dr Anthony Chaffee',
    role: 'General Practitioner · Principal Doctor',
    creds: 'MBBS · Royal College of Surgeons in Ireland',
    languages: 'English',
    focus: ['General medicine', "Men's & women's health", 'Diabetes management', 'Chronic disease management', 'Functional & preventative medicine'],
    bio:
      'With a medical degree from the Royal College of Surgeons in Ireland and a background in molecular biology, Dr Chaffee brings expertise in functional, preventative and nutritional medicine. A former neurosurgical registrar and global speaker, he has also provided humanitarian care in Bangladesh. At Elevate he leads with a focus on proactive, transformative care.',
    quote: 'True healthcare starts before illness begins. Prevention is the new cure.',
    image: 'https://customer-assets.emergentagent.com/job_elevate-refresh-1/artifacts/u1x4wszq_1D2A0039.webp',
    hasBackground: true,
    note: 'A wait-list applies for new patients. Please call the clinic on 0432 809 498 to discuss.',
  },
  {
    id: 't3',
    name: 'Dr Sam Beh',
    role: 'General Practitioner · Lifestyle Medicine',
    creds: 'FRACGP · MD · BENS · Notre Dame University',
    languages: 'English · German',
    focus: ['Chronic disease management', 'Preventative health', 'Lifestyle medicine', 'Metabolic health', 'Mental health'],
    bio:
      'Dr Sam completed his medical degree at Notre Dame University in 2018. Originally from Queensland, he completed undergraduate studies in nutrition and exercise science prior to medicine and maintains a strong interest in these areas. He has worked across various departments including a registrar role in psychiatry, and brings a whole-of-life approach to everyday general practice.',
    quote: 'Small, consistent changes are what create lasting health.',
    image: 'https://customer-assets.emergentagent.com/job_elevate-refresh-1/artifacts/9h1qotww_hf_20260710_032357_f384c73c-dad3-4b1d-9635-e6522f8df328.png',
    hasBackground: false,
    note: 'Ideal for patients wanting ongoing GP care with a lifestyle-medicine lens.',
  },
  {
    id: 't3',
    name: 'Sanjana Kaur',
    role: 'Accredited Clinical Dietitian',
    creds: 'Master of Nutrition & Dietetics \u00b7 Edith Cowan University',
    languages: 'English \u00b7 Malay',
    focus: ['Ketogenic & low-carb nutrition', 'Weight management', 'Metabolic health & insulin resistance', 'Personalised meal planning', 'Sustainable health optimisation'],
    bio:
      'Sanjana is a qualified Dietitian passionate about sustainable, evidence-based nutrition. She specialises in ketogenic and low-carbohydrate nutrition supporting weight management, metabolic health and insulin resistance. Trained under Dr Anthony Chaffee, she brings a deep understanding of therapeutic ketogenic approaches and ancestral nutrition principles.',
    quote: 'Longevity is earned through the choices we make today.',
    image: 'https://customer-assets.emergentagent.com/job_elevate-refresh-1/artifacts/e18hhly6_image%20%284%29.png',
    note: 'Consults with Sanjana can be booked online once advised by Dr Chaffee.',
  },
  {
    id: 't4',
    name: 'Ms Nuri Evrita',
    role: 'Registered Nurse · Aesthetic & Skin',
    creds: 'Bachelor of Nursing \u00b7 Postgraduate Cosmetic & Dermatology Nursing',
    languages: 'English',
    focus: ['Advanced skin treatments', 'Injectables & regenerative treatments', "Men's & women's health"],
    bio:
      'Nuri is a qualified nurse specialising in advanced skin treatments at Elevate. Drawing on her clinical background, she delivers evidence-based aesthetic care with a strong focus on safety, results and individualised treatment plans \u2014 blending medical knowledge with a holistic view of skin health.',
    quote: 'Your future health is created by today\u2019s habits.',
    image: 'https://customer-assets.emergentagent.com/job_elevate-refresh-1/artifacts/72nktagt_image%20%283%29.png',
  },
  {
    id: 't6',
    name: 'Frances Zampogna',
    role: 'Lead Medical Receptionist',
    creds: '30+ years at the clinic',
    languages: 'English',
    focus: ['Patient coordination', 'Clinic operations', 'Warm & professional care'],
    bio:
      'With over 30 years at the clinic, Frances is the trusted face of our front office. As Lead Medical Receptionist she brings unmatched experience in patient coordination and clinic operations \u2014 known for her warmth and professionalism, she ensures every patient\u2019s journey starts with clarity, comfort and care.',
    image: 'https://customer-assets.emergentagent.com/job_elevate-refresh-1/artifacts/bnx2qkbq_1D2A0015.webp',
  },
];

export const journey = [
  { step: '01', title: 'Book your assessment', desc: 'Reserve your Comprehensive Lifestyle & Medical Assessment online or by phone.' },
  { step: '02', title: 'Deep-dive consultation', desc: 'A thorough review of your history, symptoms, lifestyle and goals.' },
  { step: '03', title: 'Investigations & blood work', desc: 'Targeted diagnostics to reveal the root causes of your health picture.' },
  { step: '04', title: 'Personalised written plan', desc: 'A tailored plan built around your data — medical, nutrition and lifestyle.' },
  { step: '05', title: 'Integrated care team', desc: 'Doctor, dietitian and nurse working together on your outcomes.' },
  { step: '06', title: 'Ongoing support', desc: 'Reviews, adjustments and accountability for lasting change.' },
];

export const testimonials = [
  { id: 'tm1', name: 'Michael R.', age: '52',
    quote: 'Great customer service, great doctor!',
    image: 'https://images.unsplash.com/photo-1535643302794-19c3804b874b' },
  { id: 'tm2', name: 'Rebecca L.', age: '41',
    quote: 'THE BEST EVER WEIGHT LOSS treatment EVER!!!',
    image: 'https://images.unsplash.com/photo-1655133317714-7fc57c911439' },
  { id: 'tm3', name: 'David T.', age: '48',
    quote: 'Information was in depth and communication commendable.',
    image: 'https://images.unsplash.com/photo-1652883254451-a7c2b94cc35b' },
];

export const services = [
  { title: 'Medical Weight Loss', desc: 'Programs that lift metabolism, restore energy and reduce chronic-disease risk.' },
  { title: 'Functional Medicine', desc: 'Beyond symptoms — advanced diagnostics and root-cause treatment.' },
  { title: 'Corrective Hormonal Therapy', desc: 'Precision therapies that rebalance hormones and improve mood, sleep & energy.' },
  { title: 'Preventative Care', desc: 'Forward-focused care to protect your health before symptoms appear.' },
  { title: 'IV Therapy', desc: 'Hydration, vitamin infusions, booster shots and NAD+ for cellular vitality.' },
];

export const faqs = [
  {
    q: 'What makes Elevate different from a standard GP clinic?',
    a: 'We combine standard general practice with functional & lifestyle medicine. Every patient receives an in-depth assessment, extensive investigations and a written, personalised plan — with time to actually get to the root of the problem.',
  },
  {
    q: 'Do you bulk bill?',
    a: 'No. Elevate is a private billing practice. Fees are quoted at the time of booking and payment is required at consultation. This lets us offer longer, higher-quality consultations.',
  },
  {
    q: 'Is there a wait-list?',
    a: 'A wait-list currently applies for new patients wanting to see Dr Chaffee. Please call reception on 0432 809 498 for the latest availability.',
  },
  {
    q: 'Do I need a referral?',
    a: 'No referral is needed to book with Dr Chaffee. Existing patients may be referred internally to Sanjana Kaur (Dietitian) or Nurse Nuri as clinically appropriate.',
  },
  {
    q: 'What testing might be involved?',
    a: 'Depending on your history and goals, testing may include comprehensive blood work, hormone panels, gut / microbiome testing, body composition analysis and cardiovascular risk assessment.',
  },
  {
    q: 'Can I have Telehealth consultations?',
    a: 'Yes — Dr Chaffee’s existing patients can book Zoom appointments on Thursdays. New patients begin with an in-clinic appointment.',
  },
];

export const insights = [
  {
    id: 'i1',
    tag: 'Metabolic Health',
    title: 'Why blood-sugar spikes matter — even if you’re not diabetic',
    read: '5 min read',
    image: 'https://images.unsplash.com/photo-1606206591513-adbfbdd7a177',
  },
  {
    id: 'i2',
    tag: 'Nutrition',
    title: 'Low-carbohydrate eating: what the evidence actually says',
    read: '7 min read',
    image: 'https://images.unsplash.com/photo-1585358682246-23acb1561f6b',
  },
  {
    id: 'i3',
    tag: 'Longevity',
    title: 'The five biomarkers that predict how well you’ll age',
    read: '6 min read',
    image: 'https://images.unsplash.com/photo-1633167606207-d840b5070fc2',
  },
];

export const trustBadges = [
  { label: 'RACGP', full: 'Royal Australian College of General Practitioners' },
  { label: 'AHPRA', full: 'Registered with AHPRA' },
  { label: 'APD', full: 'Accredited Practising Dietitian' },
  { label: 'HotDoc', full: 'Verified on HotDoc' },
  { label: 'RCSI', full: 'Royal College of Surgeons in Ireland' },
  { label: 'ECU', full: 'Edith Cowan University' },
];

export const pathwayOptions = [
  'Comprehensive Lifestyle & Medical Assessment (Dr Chaffee)',
  'Dietitian consult (Sanjana Kaur)',
  'Cosmetic / Nurse consult (Nurse Nuri)',
  "I'm not sure — help me choose",
];

export const heroImage = 'https://images.pexels.com/photos/7579823/pexels-photo-7579823.jpeg';
export const gymImage = 'https://images.unsplash.com/photo-1599058917212-d750089bc07e';
